import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import {
  MdAutoAwesome,
  MdCancel,
  MdCheckCircle,
  MdExpandLess,
  MdExpandMore,
  MdOpenInNew,
  MdSearch,
  MdWarningAmber
} from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { InteractiveDiv, InteractiveSpan } from '../../../Components/InteractiveEl';
import { Appearance, Size } from '../../../Components/designConstants';
import { useRevealOnExpand } from '../../../utils/hooks/useRevealOnExpand';
import { getOptimizeBand } from './ai-optimize-utils';
import {
  AlexaAssessmentStore,
  getValidAssessment,
  isAssessmentStale
} from './alexa-assessments-utils';
import {
  ITEMS_PER_PAGE,
  SortDirection,
  SortDirectionType,
  SortField,
  SortFieldType
} from './product-score-table-constants';
import { getScoreToneClass, getStatusToneClass } from './product-score-table-utils';
import { countAcceptedFields, getProjectedProduct, ProposalStore } from './proposals-utils';
import { ScoreMode, ScoreModeType } from './scorecard-constants';
import {
  AuditedProduct,
  ScoreItem,
  ScoreStatusType,
  VIDEO_SCORING_ENABLED
} from './scoring-engine-constants';

const StatusIcon = ({ status }: { status: ScoreStatusType }) => {
  if (status === 'pass') return <MdCheckCircle className='tone-success' />;
  if (status === 'warning') return <MdWarningAmber className='tone-warning' />;
  return <MdCancel className='tone-danger' />;
};

interface ScoreItemCardProps {
  label: string;
  item: ScoreItem;
  actualLabel: string;
  activeFilterId: string | null;
  /** Bottleneck check(s) this card answers; several when one score has more
   *  than one way to fail (the title can be under the band or over the cap). */
  filterId: string | string[];
  aiApplied?: boolean;
}

const ScoreItemCard = ({
  label,
  item,
  actualLabel,
  activeFilterId,
  filterId,
  aiApplied
}: ScoreItemCardProps) => (
  <div
    className={cn('score-item-card', getStatusToneClass(item.status), {
      'is-glowing':
        activeFilterId !== null &&
        (Array.isArray(filterId) ? filterId.includes(activeFilterId) : filterId === activeFilterId)
    })}
  >
    <div className='score-item-card-header'>
      <span className='score-item-card-label'>
        {label}
        {aiApplied && <span className='score-item-card-ai-badge'>✦ AI</span>}
      </span>
      <StatusIcon status={item.status} />
    </div>
    <div className='score-item-card-value'>
      <span className={cn('score-item-card-points', getStatusToneClass(item.status))}>
        {item.score}%
      </span>
      <span className='score-item-card-max'>awarded</span>
    </div>
    <p className='score-item-card-actual'>{actualLabel}</p>
    <p className='score-item-card-criteria'>{item.criteriaText}</p>
  </div>
);

const buildPdpUrl = (product: AuditedProduct, tld: string): string => {
  // Prefer the URL the scrape itself returned (already correct for the marketplace
  // it was scraped from); only fall back to constructing one from the selected `tld`
  // when the scraped URI is missing (e.g. a CSV import with no URI column).
  if (product.uri && product.uri !== ' — ' && product.uri.startsWith('http')) {
    return product.uri;
  }
  return `https://www.amazon.${tld}/dp/${product.asin}`;
};

const SecondaryBadgesPanel = ({ product, tld }: { product: AuditedProduct; tld: string }) => (
  <div className='secondary-badges-side'>
    <div className='secondary-badges-header'>
      <span className='secondary-badges-title'>Secondary Badges</span>
    </div>

    <div className='secondary-badges-panel'>
      <div className='secondary-badge-block'>
        <div className='secondary-badge-row'>
          <span>Variations</span>
          <span
            className={cn(
              'secondary-badge-pill',
              product.variantAsins.length > 0 ? 'tone-success' : 'tone-neutral'
            )}
          >
            {product.variantAsins.length > 0 ? `YES (${product.variantAsins.length})` : 'NO'}
          </span>
        </div>
        {product.variantAsins.length > 0 && (
          <div className='secondary-badge-variants'>
            <span className='secondary-badge-variants-title'>
              {product.variantAsins.length} variants tracked:
            </span>
            <p>{product.variantAsins.join(', ')}</p>
          </div>
        )}
      </div>

      <div className='secondary-badge-row'>
        <span>Byline Brand Match</span>
        <span
          className={cn(
            'secondary-badge-pill',
            product.brandUrl !== ' — ' ? 'tone-success' : 'tone-danger'
          )}
        >
          {product.brandUrl !== ' — ' ? 'STORE FOUND' : 'NOT RECOGNIZED'}
        </span>
      </div>
      <div className='secondary-badge-row'>
        <span>Climate Pledge</span>
        <span
          className={cn(
            'secondary-badge-pill',
            product.climatePledgeFriendly ? 'tone-success' : 'tone-neutral'
          )}
        >
          {product.climatePledgeFriendly ? 'YES' : 'NO'}
        </span>
      </div>
      <div className='secondary-badge-row'>
        <span>Amazon&apos;s Choice</span>
        <span
          className={cn(
            'secondary-badge-pill',
            product.amazonsChoiceFlag ? 'tone-brand' : 'tone-neutral'
          )}
        >
          {product.amazonsChoiceFlag ? 'YES' : 'NO'}
        </span>
      </div>
      <div className='secondary-badge-row'>
        <span>Best Seller Tag</span>
        <span
          className={cn(
            'secondary-badge-pill',
            product.bestSellerFlag ? 'tone-warning' : 'tone-neutral'
          )}
        >
          {product.bestSellerFlag ? 'YES' : 'NO'}
        </span>
      </div>
      <div className='secondary-badge-row'>
        <span>Prime Enrolled</span>
        <span
          className={cn('secondary-badge-pill', product.primeFlag ? 'tone-brand' : 'tone-neutral')}
        >
          {product.primeFlag ? 'YES' : 'NO'}
        </span>
      </div>
    </div>

    {product.asin && (
      <a
        className='secondary-badge-pdp-button'
        href={buildPdpUrl(product, tld)}
        target='_blank'
        rel='noreferrer'
      >
        View Live Amazon PDP <MdOpenInNew />
      </a>
    )}
  </div>
);

const ProductScoreAccordion = ({
  effective,
  activeFilterId,
  tld
}: {
  effective: AuditedProduct;
  activeFilterId: string | null;
  tld: string;
}) => {
  const truncatedBulletCount = effective.featureBullets.filter(b => b.length > 150).length;
  return (
    <div className='product-score-accordion'>
      <div className='product-score-accordion-main'>
        <div className='product-score-accordion-scores'>
          <div className='score-board-header'>
            <span className='score-board-title'>Detailed Content Audit Scoreboard</span>
            <span className='score-board-total'>
              Total: {effective.finalScore} / 100 maximum points Mapped
            </span>
          </div>
          <div className='score-item-grid'>
            <ScoreItemCard
              label='Feature Bullets (15% Max)'
              item={effective.bulletsScore}
              actualLabel={`Actual count: ${effective.bulletsScore.actualValue} bullets`}
              activeFilterId={activeFilterId}
              filterId='bullets_less_5'
            />
            <ScoreItemCard
              label='Title Length (25% Max)'
              item={effective.titleScore}
              actualLabel={`Actual length: ${effective.titleScore.actualValue} characters`}
              activeFilterId={activeFilterId}
              filterId={['title_short', 'title_over_cap']}
            />
            <ScoreItemCard
              label='Description Length (5% Max)'
              item={effective.descriptionScore}
              actualLabel={`Actual length: ${effective.descriptionScore.actualValue} characters`}
              activeFilterId={activeFilterId}
              filterId='desc_short'
            />
            <ScoreItemCard
              label='Images Count (15% Max)'
              item={effective.imagesScore}
              actualLabel={`Actual count: ${effective.imagesScore.actualValue} image assets`}
              activeFilterId={activeFilterId}
              filterId='images_less_5'
            />
            <ScoreItemCard
              label='Star Rating (10% Max)'
              item={effective.ratingScore}
              actualLabel={`Average stars: ${effective.ratingScore.actualValue}`}
              activeFilterId={activeFilterId}
              filterId='low_ratings'
            />
            <ScoreItemCard
              label='Total Reviews (15% Max)'
              item={effective.reviewsScore}
              actualLabel={`Actual review count: ${effective.reviewsScore.actualValue}`}
              activeFilterId={activeFilterId}
              filterId='reviews_less_100'
            />
            {/* Videos dimension hidden while the scraper does not supply video data
            (VIDEO_SCORING_ENABLED). Restoring the flag brings this row back. */}
            {VIDEO_SCORING_ENABLED && (
              <ScoreItemCard
                label='Videos Count (5% Max)'
                item={effective.videosScore}
                actualLabel={`Videos uploaded: ${effective.videosScore.actualValue}`}
                activeFilterId={activeFilterId}
                filterId='no_videos'
              />
            )}
            <ScoreItemCard
              label='A+ Content Present (10% Max)'
              item={effective.aplusScore}
              actualLabel={`A+ Layout Status: ${effective.aplusScore.actualValue}`}
              activeFilterId={activeFilterId}
              filterId='aplus_missing'
            />
          </div>
        </div>
        <SecondaryBadgesPanel product={effective} tld={tld} />
      </div>
      <div className='product-score-accordion-content'>
        <div className='content-preview-card'>
          <div className='content-preview-header'>
            <h5>Feature Bullets ({effective.featureBullets.length} parsed)</h5>
            {truncatedBulletCount > 0 && (
              <span
                className='content-preview-truncated-badge'
                title='Mobile Alexa for Shopping truncates bullets after 150 chars — these will be cut off'
              >
                <MdWarningAmber /> {truncatedBulletCount} mobile-truncated
              </span>
            )}
          </div>
          {effective.featureBullets.length === 0 ? (
            <p className='content-preview-empty'>No feature bullets found in scraper row cells.</p>
          ) : (
            <ul className='content-preview-bullets'>
              {effective.featureBullets.map((bullet, idx) =>
                bullet.length > 150 ? (
                  <li key={idx}>
                    {bullet.slice(0, 150)}
                    <span
                      className='content-preview-bullet-overflow'
                      title={`Mobile cutoff — ${bullet.length - 150} chars truncated on mobile Alexa for Shopping`}
                    >
                      {bullet.slice(150)}
                    </span>
                  </li>
                ) : (
                  <li key={idx}>{bullet}</li>
                )
              )}
            </ul>
          )}
        </div>
        <div className='content-preview-card'>
          <div className='content-preview-header'>
            <h5>Full Product Description ({effective.fullDescription.length} characters)</h5>
          </div>
          {effective.fullDescription && effective.fullDescription !== ' — ' ? (
            <p>{effective.fullDescription}</p>
          ) : (
            <p className='content-preview-empty'>
              No description string found in scraper row cells.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

interface RowScoreCellProps {
  isAlexa: boolean;
  product: AuditedProduct;
  projected: AuditedProduct | null;
  hasActiveProposal: boolean;
  assessment: ReturnType<typeof getValidAssessment>;
  assessmentStale: boolean;
}

const RowScoreCell = ({
  isAlexa,
  product,
  projected,
  hasActiveProposal,
  assessment,
  assessmentStale
}: RowScoreCellProps) => {
  if (isAlexa) {
    if (!assessment) {
      return (
        <span className='score-not-assessed'>{assessmentStale ? 'Re-assess' : 'Not assessed'}</span>
      );
    }
    return (
      <>
        <span className={cn('score-pill', getScoreToneClass(assessment.score, ScoreMode.ALEXA))}>
          {assessment.score}%
        </span>
        <div className='score-meter'>
          <div
            className={cn('score-meter-fill', getScoreToneClass(assessment.score, ScoreMode.ALEXA))}
            style={{ width: `${assessment.score}%` }}
          />
        </div>
      </>
    );
  }

  if (hasActiveProposal && projected) {
    return (
      <>
        <span className='score-pill score-pill-projected'>
          <span className='score-pill-before'>{product.finalScore}</span> →{' '}
          <span className={getScoreToneClass(projected.finalScore, ScoreMode.HEALTH)}>
            {projected.finalScore}%
          </span>
        </span>
        <div className='score-meter'>
          <div
            className={cn(
              'score-meter-fill',
              getScoreToneClass(projected.finalScore, ScoreMode.HEALTH)
            )}
            style={{ width: `${projected.finalScore}%` }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <span className={cn('score-pill', getScoreToneClass(product.finalScore, ScoreMode.HEALTH))}>
        {product.finalScore}%
      </span>
      <div className='score-meter'>
        <div
          className={cn(
            'score-meter-fill',
            getScoreToneClass(product.finalScore, ScoreMode.HEALTH)
          )}
          style={{ width: `${product.finalScore}%` }}
        />
      </div>
    </>
  );
};

interface RowActionsCellProps {
  isAlexa: boolean;
  product: AuditedProduct;
  effective: AuditedProduct;
  hasActiveProposal: boolean;
  acceptedCount: number;
  assessment: ReturnType<typeof getValidAssessment>;
  assessmentStale: boolean;
  onOptimize: (product: AuditedProduct) => void;
  onAssess: (product: AuditedProduct) => void;
}

const RowActionsCell = ({
  isAlexa,
  product,
  effective,
  hasActiveProposal,
  acceptedCount,
  assessment,
  assessmentStale,
  onOptimize,
  onAssess
}: RowActionsCellProps) => {
  if (isAlexa) {
    let label = 'Assess';
    if (assessment) {
      label = 'View readiness';
    } else if (assessmentStale) {
      label = 'Re-assess';
    }
    return (
      <Button
        appearance={hasActiveProposal || assessment ? Appearance.OUTLINED : Appearance.FILLED}
        size={Size.SM}
        leadingElement={<MdAutoAwesome />}
        onClick={e => {
          e.stopPropagation();
          onAssess(effective);
        }}
      >
        {label}
      </Button>
    );
  }

  if (hasActiveProposal) {
    return (
      <Button
        appearance={Appearance.OUTLINED}
        size={Size.SM}
        leadingElement={<MdAutoAwesome />}
        onClick={e => {
          e.stopPropagation();
          onOptimize(product);
        }}
      >
        Review ({acceptedCount})
      </Button>
    );
  }

  const band = getOptimizeBand(product.finalScore);
  if (band === 'optimized') {
    return (
      <span className='score-optimized-badge'>
        <MdCheckCircle /> Optimized
      </span>
    );
  }

  return (
    <Button
      appearance={band === 'polish' ? Appearance.OUTLINED : Appearance.FILLED}
      size={Size.SM}
      leadingElement={<MdAutoAwesome />}
      onClick={e => {
        e.stopPropagation();
        onOptimize(product);
      }}
    >
      {band === 'polish' ? 'Refine' : 'Optimize'}
    </Button>
  );
};

interface ProductScoreRowProps {
  product: AuditedProduct;
  activeFilterId: string | null;
  scoreMode: ScoreModeType;
  proposals: ProposalStore;
  assessments: AlexaAssessmentStore;
  tld: string;
  onOptimize: (product: AuditedProduct) => void;
  onAssess: (product: AuditedProduct) => void;
}

const ProductScoreRow = ({
  product,
  activeFilterId,
  scoreMode,
  proposals,
  assessments,
  tld,
  onOptimize,
  onAssess
}: ProductScoreRowProps) => {
  const [expanded, setExpanded] = useState(false);
  // Reveal the accordion when it opens below the fold — see the hook's comment.
  const wrapperRef = useRevealOnExpand<HTMLDivElement>(expanded);
  const isAlexa = scoreMode === ScoreMode.ALEXA;
  const proposal = proposals[product.id];
  const projected = getProjectedProduct(product, proposal);
  const acceptedCount = countAcceptedFields(proposal);
  const hasActiveProposal = projected !== null && acceptedCount > 0;
  const effective = projected ?? product;
  const assessment = getValidAssessment(assessments, effective);
  const assessmentStale = isAssessmentStale(assessments, effective);

  return (
    <div ref={wrapperRef} className={cn('product-score-row-wrapper', { 'is-expanded': expanded })}>
      <InteractiveDiv className='product-score-row' onClick={() => setExpanded(v => !v)}>
        <div className='product-score-row-info'>
          {/* Wraps in full now, so no `title` tooltip repeating it. */}
          <div className='product-score-row-title'>
            {product.title || <span className='is-empty'>No Title Asset Provided</span>}
          </div>
          <div className='product-score-row-meta'>
            <span className='product-score-row-asin'>{product.asin || '—'}</span>
            <span>&bull;</span>
            <span className='product-score-row-brand'>{product.brand || 'Independent'}</span>
          </div>
        </div>
        <div className='product-score-row-category' title={product.category || 'General Products'}>
          {product.category || 'General Products'}
        </div>
        <div className='product-score-row-score'>
          <RowScoreCell
            isAlexa={isAlexa}
            product={product}
            projected={projected}
            hasActiveProposal={hasActiveProposal}
            assessment={assessment}
            assessmentStale={assessmentStale}
          />
        </div>
        <div className='product-score-row-actions'>
          {!isAlexa && (product.failCount > 0 || product.warningCount > 0) && (
            <div className='product-score-row-flags'>
              {product.failCount > 0 && (
                <span
                  className='product-score-row-flag is-fail'
                  title={`${product.failCount} failing check${product.failCount === 1 ? '' : 's'}`}
                >
                  <span className='product-score-row-flag-dot' />
                  {product.failCount}
                </span>
              )}
              {product.warningCount > 0 && (
                <span
                  className='product-score-row-flag is-warn'
                  title={`${product.warningCount} warning${product.warningCount === 1 ? '' : 's'}`}
                >
                  <span className='product-score-row-flag-dot' />
                  {product.warningCount}
                </span>
              )}
            </div>
          )}
          <RowActionsCell
            isAlexa={isAlexa}
            product={product}
            effective={effective}
            hasActiveProposal={hasActiveProposal}
            acceptedCount={acceptedCount}
            assessment={assessment}
            assessmentStale={assessmentStale}
            onOptimize={onOptimize}
            onAssess={onAssess}
          />
          {expanded ? <MdExpandLess /> : <MdExpandMore />}
        </div>
      </InteractiveDiv>
      {expanded && (
        <ProductScoreAccordion effective={effective} activeFilterId={activeFilterId} tld={tld} />
      )}
    </div>
  );
};

interface ProductScoreTableProps {
  products: AuditedProduct[];
  activeFilterId: string | null;
  scoreMode: ScoreModeType;
  proposals: ProposalStore;
  assessments: AlexaAssessmentStore;
  tld: string;
  onOptimize: (product: AuditedProduct) => void;
  onAssess: (product: AuditedProduct) => void;
}

// Typed accessor for the sortable columns, in place of an `as never` cast against
// AuditedProduct — PROJECTED_LIFT is computed separately by `liftFor` in the caller.
const getSortValue = (product: AuditedProduct, field: SortFieldType): number | string => {
  switch (field) {
    case SortField.ASIN:
      return product.asin;
    case SortField.BRAND:
      return product.brand;
    case SortField.TITLE:
      return product.title;
    case SortField.TOTAL_REVIEWS:
      return product.totalReviews;
    case SortField.FINAL_SCORE:
    case SortField.PROJECTED_LIFT:
    default:
      return product.finalScore;
  }
};

export const ProductScoreTable = ({
  products,
  activeFilterId,
  scoreMode,
  proposals,
  assessments,
  tld,
  onOptimize,
  onAssess
}: ProductScoreTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortFieldType>(SortField.FINAL_SCORE);
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.ASC);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field: SortFieldType) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.DESC);
    }
    setCurrentPage(1);
  };

  const processedProducts = useMemo(() => {
    const s = searchQuery.toLowerCase();
    const filtered = products.filter(
      p =>
        p.asin.toLowerCase().includes(s) ||
        p.brand.toLowerCase().includes(s) ||
        p.title.toLowerCase().includes(s) ||
        p.category.toLowerCase().includes(s)
    );

    const liftFor = (product: AuditedProduct): number => {
      const projected = getProjectedProduct(product, proposals[product.id]);
      return projected ? projected.finalScore - product.finalScore : 0;
    };

    filtered.sort((a, b) => {
      const aVal = sortField === SortField.PROJECTED_LIFT ? liftFor(a) : getSortValue(a, sortField);
      const bVal = sortField === SortField.PROJECTED_LIFT ? liftFor(b) : getSortValue(b, sortField);
      if (aVal < bVal) return sortDirection === SortDirection.ASC ? -1 : 1;
      if (aVal > bVal) return sortDirection === SortDirection.ASC ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [products, searchQuery, sortField, sortDirection, proposals]);

  const totalPages = Math.ceil(processedProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(
    () => processedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [processedProducts, currentPage]
  );

  // The underlying `products` prop can shrink out from under an already-set page
  // (e.g. a deficiency filter is applied elsewhere) without going through the
  // search/sort handlers that reset currentPage to 1, leaving a stale page number
  // that renders an empty "no matching listings" state with pagination hidden.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className='product-score-table'>
      <div className='product-score-table-controls'>
        <span className='product-score-table-count'>
          {processedProducts.length} {processedProducts.length === 1 ? 'listing' : 'listings'}
        </span>
        <Form.Control
          className='product-score-table-search'
          type='text'
          placeholder='Search ASIN, title, brand...'
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className='product-score-table-header'>
        <div className='product-score-table-header-product'>
          <InteractiveSpan onClick={() => handleSort(SortField.TITLE)}>
            Product / ASIN{' '}
            {sortField === SortField.TITLE && (sortDirection === SortDirection.ASC ? '▲' : '▼')}
          </InteractiveSpan>
          <span className='product-score-table-header-sep'>/</span>
          <InteractiveSpan onClick={() => handleSort(SortField.BRAND)}>
            Brand{' '}
            {sortField === SortField.BRAND && (sortDirection === SortDirection.ASC ? '▲' : '▼')}
          </InteractiveSpan>
        </div>
        <span>Category</span>
        <InteractiveSpan
          className='product-score-table-header-score'
          onClick={() => handleSort(SortField.FINAL_SCORE)}
        >
          {scoreMode === ScoreMode.ALEXA ? 'Alexa Readiness' : 'Health Score'}
        </InteractiveSpan>
        <span className='product-score-table-header-actions'>
          {scoreMode === ScoreMode.ALEXA ? 'Readiness / Actions' : 'Critique / Actions'}
        </span>
      </div>

      {paginatedProducts.length === 0 ? (
        <div className='product-score-table-empty'>
          <MdSearch />
          <p>No matching listings found</p>
        </div>
      ) : (
        paginatedProducts.map(p => (
          <ProductScoreRow
            key={p.id}
            product={p}
            activeFilterId={activeFilterId}
            scoreMode={scoreMode}
            proposals={proposals}
            assessments={assessments}
            tld={tld}
            onOptimize={onOptimize}
            onAssess={onAssess}
          />
        ))
      )}

      {totalPages > 1 && (
        <div className='product-score-table-pagination'>
          <span>
            Page {currentPage} of {totalPages} ({processedProducts.length} entries)
          </span>
          <div className='product-score-table-pagination-buttons'>
            <Button
              appearance={Appearance.OUTLINED}
              size={Size.SM}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
            >
              Previous
            </Button>
            <Button
              appearance={Appearance.OUTLINED}
              size={Size.SM}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
